import React, {useState, useRef} from 'react';
import {
    Paper, InputBase, IconButton, Fade, Popper, List, ListItem, ListItemText,
    Typography, Chip, CircularProgress, Alert
} from '@mui/material';
import {
    Search as SearchIcon, Clear as ClearIcon, MyLocation as MyLocationIcon,
    Place as PlaceIcon
} from '@mui/icons-material';

const MapSearchControl = ({ mapRef, onLocationSelect, embedded = false }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [error, setError] = useState('');
    const searchRef = useRef(null);
    const anchorRef = useRef(null);

    // Nominatim geocoding service (free, no API key required)
    const searchLocation = async (searchQuery) => {
        if (!searchQuery.trim()) {
            setResults([]);
            setShowResults(false);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&limit=8&q=${encodeURIComponent(searchQuery)}&addressdetails=1`
            );

            if (!response.ok) {
                throw new Error('Search service unavailable');
            }

            const data = await response.json();
            setResults(data);
            setShowResults(data.length > 0);

            if (data.length === 0) {
                setError('No locations found');
            }
        } catch (err) {
            console.error('Geocoding error:', err);
            setError('Search failed. Please try again.');
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (event) => {
        const value = event.target.value;
        setQuery(value);
        
        // Debounce search
        clearTimeout(window.searchTimeout);
        window.searchTimeout = setTimeout(() => {
            searchLocation(value);
        }, 500);
    };

    const handleLocationClick = (result) => {
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        
        // Move map to location
        if (mapRef.current) {
            mapRef.current.setView([lat, lon], 14);
        }
        
        // Update search input
        setQuery(result.display_name.split(',').slice(0, 3).join(', '));
        setShowResults(false);
        
        // Callback for parent component
        if (onLocationSelect) {
            onLocationSelect({ lat, lon, name: result.display_name });
        }
    };

    const handleClear = () => {
        setQuery('');
        setResults([]);
        setShowResults(false);
        setError('');
        if (searchRef.current) {
            searchRef.current.focus();
        }
    };

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError('Geolocation not supported');
            return;
        }

        setIsLoading(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lon = position.coords.longitude;
                
                if (mapRef.current) {
                    mapRef.current.setView([lat, lon], 14);
                }
                
                setQuery('Current location');
                setIsLoading(false);
                
                if (onLocationSelect) {
                    onLocationSelect({ lat, lon, name: 'Current location' });
                }
            },
            (error) => {
                console.error('Geolocation error:', error);
                setError('Location access denied');
                setIsLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const formatDisplayName = (displayName) => {
        // Split and take first 3 parts for cleaner display
        const parts = displayName.split(',');
        const mainPart = parts.slice(0, 2).join(', ');
        const country = parts[parts.length - 1]?.trim();
        
        return {
            main: mainPart,
            country: country
        };
    };

    const containerStyle = embedded ? {
        position: 'relative',
        width: '100%'
    } : {
        position: 'absolute',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1001,
        maxWidth: '400px',
        width: '100%'
    };

    const paperStyle = embedded ? {
        display: 'flex',
        alignItems: 'center',
        p: '8px 12px',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'grey.50'
        }
    } : {
        display: 'flex',
        alignItems: 'center',
        p: '8px 12px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(12px)',
        borderRadius: 3,
        border: '1px solid rgba(226, 232, 240, 0.6)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }
    };

    return (
        <div style={containerStyle}>
            <Paper
                ref={anchorRef}
                elevation={embedded ? 1 : 3}
                sx={paperStyle}
            >
                <SearchIcon sx={{ m: 1, color: 'action.active' }} />
                <InputBase
                    ref={searchRef}
                    placeholder="Go to location..."
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => results.length > 0 && setShowResults(true)}
                    sx={{ ml: 1, flex: 1 }}
                    inputProps={{ 'aria-label': 'go to location' }}
                />
                {isLoading && (
                    <CircularProgress size={20} sx={{ m: 1 }} />
                )}
                {query && !isLoading && (
                    <IconButton 
                        onClick={handleClear}
                        size="small"
                        aria-label="clear search"
                    >
                        <ClearIcon />
                    </IconButton>
                )}
                <IconButton 
                    onClick={getCurrentLocation}
                    size="small"
                    aria-label="current location"
                    color="primary"
                >
                    <MyLocationIcon />
                </IconButton>
            </Paper>

            {/* Search Results Dropdown */}
            <Popper
                open={showResults && !isLoading}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                transition
                style={{ zIndex: 1001, width: anchorRef.current?.offsetWidth }}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps}>
                        <Paper elevation={8} sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
                            <List dense>
                                {results.map((result, index) => {
                                    const formatted = formatDisplayName(result.display_name);
                                    return (
                                        <ListItem
                                            key={`${result.place_id}-${index}`}
                                            button
                                            onClick={() => handleLocationClick(result)}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <PlaceIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" noWrap>
                                                        {formatted.main}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {formatted.country && (
                                                            <Chip 
                                                                label={formatted.country} 
                                                                size="small" 
                                                                variant="outlined"
                                                                sx={{ height: '20px', fontSize: '0.7rem' }}
                                                            />
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                            {result.type}
                                                        </Typography>
                                                    </div>
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Paper>
                    </Fade>
                )}
            </Popper>

            {/* Error Message */}
            {error && (
                <Alert 
                    severity="warning" 
                    sx={{ mt: 1, fontSize: '0.8rem' }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}
        </div>
    );
};

export default MapSearchControl;